import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useNotification } from "../contexts/NotificationContext";
import { NotificationType } from "../../../constants/Enums";
import "../styles/ConfirmationPopup.css";
import "./styles/ReportContentModal.css";

const MIN_REASON_LENGTH = 30;

export type ReportableContentType = "listing" | "business";

interface ReportContentModalProps {
  isOpen: boolean;
  /** What's being reported — drives the copy (title/placeholder). */
  contentType: ReportableContentType;
  /** Listing title or business name, shown in the modal heading. */
  targetName: string;
  onClose: () => void;
}

const COPY: Record<ReportableContentType, { heading: string; message: string }> = {
  listing: {
    heading: "Report this listing",
    message: "Tell us what's wrong (e.g. prohibited item, misleading price, scam). Our team will review it.",
  },
  business: {
    heading: "Report this business",
    message: "Tell us what's wrong (e.g. fake storefront, scam, abusive behavior). Our team will review it.",
  },
};

/**
 * Generic "report this content" modal, shared by product listings and
 * business pages. Mirrors BlockUserButton's report-user modal (same
 * min-length reason validation + tone) but is content-scoped rather than
 * user-scoped, so it lives in shared/ instead of being duplicated per page.
 *
 * No backend endpoint exists for content reports yet — same as the existing
 * "report user" flow in BlockUserButton, this captures the reason and
 * confirms locally. Swap handleSubmit's body for a real API call once the
 * backend contract lands; the structured reason text is already collected.
 */
const ReportContentModal: React.FC<ReportContentModalProps> = ({
  isOpen,
  contentType,
  targetName,
  onClose,
}) => {
  const [reason, setReason] = useState("");
  const [reasonError, setReasonError] = useState("");
  const { triggerNotification } = useNotification();

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
      setReason("");
      setReasonError("");
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const copy = COPY[contentType];
  const charsLeft = Math.max(0, MIN_REASON_LENGTH - reason.trim().length);

  const handleReasonChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setReason(e.target.value);
    if (reasonError && e.target.value.trim().length >= MIN_REASON_LENGTH) {
      setReasonError("");
    }
  };

  const handleSubmit = () => {
    if (reason.trim().length < MIN_REASON_LENGTH) {
      setReasonError(`Please provide at least ${MIN_REASON_LENGTH} characters.`);
      return;
    }
    onClose();
    triggerNotification(
      "Report submitted. Thank you for helping keep Nine safe.",
      NotificationType.SUCCESS
    );
  };

  return createPortal(
    <div className="confirmation-overlay" onClick={onClose}>
      <div className="confirmation-card rcm-card" onClick={(e) => e.stopPropagation()}>
        <h2 className="confirmation-title">{copy.heading}</h2>
        <p className="confirmation-message">
          {targetName ? `"${targetName}" — ${copy.message}` : copy.message}
        </p>

        <div className="rcm-reason-field">
          <textarea
            className={`rcm-textarea ${reasonError ? "rcm-textarea--error" : ""}`}
            placeholder="Describe the issue…"
            value={reason}
            onChange={handleReasonChange}
            rows={4}
            autoFocus
          />
          <div className="rcm-reason-meta">
            {reasonError ? (
              <span className="rcm-reason-error">{reasonError}</span>
            ) : (
              <span className="rcm-reason-hint">
                {charsLeft > 0 ? `${charsLeft} more characters needed` : "Looks good!"}
              </span>
            )}
            <span className="rcm-reason-count">{reason.trim().length}</span>
          </div>
        </div>

        <div className="confirmation-btn-row">
          <button className="confirmation-btn-cancel" onClick={onClose}>
            Cancel
          </button>
          <button className="confirmation-btn-confirm" onClick={handleSubmit}>
            Submit Report
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default ReportContentModal;
